import React from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';

import { overlayLayers, overlayLegends } from 'constants/imagery';

// Utils
import { logError } from 'api/errors';

// Selectors
import {
  getActiveOverlayData,
  getActiveOverlayLayer,
  getActiveScript,
  isOverlayActive
} from 'current/selectors';
import {
  getCropForScript,
  getScriptTypeForScript
} from 'scripts/selectors';
import { getProductValidationValues } from 'user/selectors';
import { getNominalWeightByCropId } from 'crops/selectors';

// Action Creators
import { loadActiveFieldViewImageLegend } from 'current';
import { toggleEditLegend } from '../legends';

// Components
import OverlayLegend from 'mapping/legends/OverlayLegend';
import ProductRateLegend from 'mapping/legends/ProductRateLegend';
import SoilLegendContainer from 'mapping/legends/SoilLegendContainer';
import ProductRateComparisonLayer from 'mapping/layers/ProductRateComparisonLayer';
import { ImageLayer, TileLayer } from 'mapping/leaflet';
import SoilComparisonLayer from 'mapping/layers/SoilComparisonLayer';

// Returns both an Overlay layer and an Overlay legend based on current state

let AppropriateOverlayContainer = ({ // NOSONAR False-Positive Case statement
  legendCropNominalWeight,
  loadFieldViewImageLegend,
  map, // Injected by leaflet, must be passed to Layers or they don't work
  overlayData,
  overlayLayer,
  productValidationValues,
  script,
  scriptType,
  showOverlay,
  toggleEditLegend
}) => {
  let layer = null;
  let legend = null;

  if(!showOverlay) { return null; }

  switch(overlayLayer) { // TODO: stick a data-type in the imagery data and use that instead
    case overlayLayers.PRODUCT_RATE:
      legend = (<ProductRateLegend />);
      layer = (
        <ProductRateComparisonLayer
          map={map} productValidationValues={productValidationValues} script={script} scriptType={scriptType}
        />
      );
      break;
    case overlayLayers.VEGETATION:
    case overlayLayers.SCOUTING:
      legend = (<OverlayLegend legend={overlayLegends[overlayData.layer]}/>);
      layer = (<ImageLayer bounds={overlayData.bounds} map={map} opacity={100} spinner={true} url={overlayData.url} />);
      break;
    case overlayLayers.TRUE_COLOR:
      // no legend
      layer = (<ImageLayer bounds={overlayData.bounds} map={map} opacity={100} spinner={true} url={overlayData.url} />);
      break;
    case overlayLayers.OTHER:
      legend = (<OverlayLegend legend={overlayData.legend} />);
      layer = (<ImageLayer bounds={overlayData.bounds} map={map} opacity={100} spinner={true} url={overlayData.url} />);
      break;
    case overlayLayers.SOIL_SAMPLE_CEC:
    case overlayLayers.SOIL_SAMPLE_EC:
    case overlayLayers.SOIL_SAMPLE_OM:
    case overlayLayers.SOIL_SAMPLE_P:
    case overlayLayers.SOIL_SAMPLE_K:
    case overlayLayers.SOIL_SAMPLE_PH:
      legend = (<OverlayLegend legend={overlayData.legend} />);
      layer = (<ImageLayer bounds={overlayData.bounds} map={map} opacity={100} spinner={true} url={overlayData.url} />);
      break;
    case overlayLayers.SOIL:
      legend = (<SoilLegendContainer />);
      layer = (<SoilComparisonLayer map={map} zones={overlayData} />);
      break;
    case overlayLayers.PLANTER_SPEED:
    case overlayLayers.COMBINE_SPEED:
    case overlayLayers.ELEVATION:
    case overlayLayers.MOISTURE:
    case overlayLayers.YIELD:
    case overlayLayers.ACTUAL_RATE: // Population
      if( !overlayData.legend ) {
        loadFieldViewImageLegend();
      } else {
        legend = overlayData.legend && (
          <OverlayLegend
            cropNominalWeight={legendCropNominalWeight}
            isEditable={true}
            legend={overlayData.legend}
            toggleEditLegend={toggleEditLegend} />
        );
      }
      layer = (<TileLayer map={map} opacity={100} spinner={true} url={overlayData.url}/>);
      break;
    case overlayLayers.HYBRID:
      if( !overlayData.legend ) {
        loadFieldViewImageLegend();
      } else {
        legend = overlayData.legend && (
          <OverlayLegend cropNominalWeight={legendCropNominalWeight} legend={overlayData.legend} />
        );
      }
      layer = (<TileLayer map={map} opacity={100} spinner={true} url={overlayData.url}/>);
      break;
    default:
      logError({message:`unknown overlay type: ${overlayLayer}`});
      break;
  }

  return (
    <div>
      {layer}
      {legend}
    </div>
  );
};

AppropriateOverlayContainer.propTypes = {
  legendCropNominalWeight: propTypes.number,
  loadFieldViewImageLegend: propTypes.func.isRequired,
  map: propTypes.object.isRequired, // Injected by leaflet, must be passed to Layers or they don't work
  overlayData: propTypes.object,
  overlayLayer: propTypes.string,
  productValidationValues: propTypes.object,
  script: propTypes.object,
  scriptType: propTypes.string.isRequired,
  showOverlay: propTypes.bool,
  toggleEditLegend: propTypes.func
};

AppropriateOverlayContainer = connect(
  (state) => {
    const script = getActiveScript(state);
    const scriptType = getScriptTypeForScript(script);
    const overlayLayer = getActiveOverlayLayer(state);
    const overlayData = getActiveOverlayData(state);
    const showOverlay = overlayData && isOverlayActive(state);
    return {
      crop: script && getCropForScript(script),
      legendCropNominalWeight: overlayData
        && overlayData.cropId
        && getNominalWeightByCropId(state, overlayData.cropId),
      productValidationValues: getProductValidationValues(state),
      script,
      scriptType,
      showOverlay,
      overlayLayer,
      overlayData,
    };
  },
  (dispatch) => {
    return {
      loadFieldViewImageLegend: () => dispatch( loadActiveFieldViewImageLegend() ),
      toggleEditLegend: () => dispatch( toggleEditLegend() )
    };
  }
)(AppropriateOverlayContainer);

export default AppropriateOverlayContainer;
